import { useAuth } from "@elit-hair/elit-tools";
import {
    Card,
    CardContent,
    DatePickerMode,
    DateRangePicker,
    EmptyState,
    Icon,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { TbChartBar } from "react-icons/tb";

import { PERMISSIONS } from "@/constants/permissions";
import { usePriorityRecordCounts } from "@/queries/lead.queries";
import { convertFromDateRange } from "@/utils/format-date";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { PriorityReportDeptSelector } from "./components/priority-report-dept-selector";
import { usePriorityReportDepartments } from "./hooks/use-priority-report-departments";
import {
    PRIORITY_THRESHOLDS,
    REPORT_COLORS,
    buildPriorityReportCountFilters,
    buildPriorityTableData,
    buildTotalsByDepartment,
    getCellColor,
    getPriorityReportMaxLevel,
} from "./utils/priority-report.utils";

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_SETTING_PRIORITY_REPORT)({
    component: SettingsPriorityReport,
    staticData: {
        permissions: [PERMISSIONS.CALL_SETTING_PRIORITY_COUNTS],
    },
});

function SettingsPriorityReport() {
    const { t } = useTranslation();
    const { userData } = useAuth();
    const navigate = Route.useNavigate();
    const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
    const [dateMode, setDateMode] = useState<DatePickerMode>(DatePickerMode.RANGE);

    const { activeDepartments } = usePriorityReportDepartments();

    const maxPriority = useMemo(
        () => getPriorityReportMaxLevel(userData?.permissionCodes),
        [userData?.permissionCodes]
    );

    const visiblePriorities = useMemo(
        () => Array.from({ length: maxPriority }, (_, index) => index + 1),
        [maxPriority]
    );

    const { startDate, endDate } = useMemo(() => convertFromDateRange(dateRange), [dateRange]);

    const countFilters = useMemo(() => buildPriorityReportCountFilters(activeDepartments), [activeDepartments]);

    const { data: priorityRecordCounts = [], isLoading } = usePriorityRecordCounts({
        start: startDate,
        end: endDate,
        filters: countFilters,
    });

    const activeKeys = useMemo(() => new Set(activeDepartments.map((dept) => dept.key)), [activeDepartments]);

    const tableData = useMemo(
        () => buildPriorityTableData(priorityRecordCounts, activeDepartments, maxPriority),
        [priorityRecordCounts, activeDepartments, maxPriority]
    );

    const totalsByDepartment = useMemo(() => buildTotalsByDepartment(tableData), [tableData]);

    const totalRecords = useMemo(
        () =>
            priorityRecordCounts
                .filter((item) => activeKeys.has(item.language) && item.priority <= maxPriority)
                .reduce((sum, item) => sum + item.count, 0),
        [priorityRecordCounts, activeKeys, maxPriority]
    );

    const handleOpenSettingPriorityLeadsPage = (deptKey: string, priority: number) => {
        navigate({
            to: APP_ROUTES_TO.PERFORMANCE_SETTING_PRIORITY_LEADS,
            params: { language: deptKey, priority: priority.toString() },
        });
    };

    return (
        <div className="bg-background p-4 md:p-6">
            <div className="mx-auto max-w-6xl space-y-4">
                {/* Header and Filters */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                <Icon icon={TbChartBar} size={18} className="text-primary" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold">{t("priority_report") || "Priority Report"}</h1>
                                <p className="text-xs text-muted-foreground">
                                    {t("total_records") || "Total"}:{" "}
                                    <span className="font-semibold">{totalRecords.toLocaleString()}</span>
                                </p>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full md:w-auto">
                            <PriorityReportDeptSelector variant="inline" />
                            <div className="w-full sm:w-[280px]">
                                <DateRangePicker
                                    dateRange={dateRange}
                                    onDateRangeChange={setDateRange}
                                    showModeSwitcher
                                    mode={dateMode}
                                    onModeChange={setDateMode}
                                    maxDate={DateTime.now().toJSDate()}
                                    numberOfMonths={2}
                                    showTodayButton
                                    showClearButton
                                    placeholder={t("select_date_range")}
                                    buttonWidth="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Compact Table Card */}
                <Card className="border-border/40 shadow-sm relative min-h-[200px]">
                    {isLoading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px]">
                            <Spinner size="lg" />
                        </div>
                    )}
                    <CardContent className="p-4">
                        {activeDepartments.length === 0 && !isLoading ? (
                            <EmptyState variant="simple" title="priority_report_select_collection" />
                        ) : (
                            <div className="overflow-auto rounded-lg border border-border/40">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="w-12 font-semibold text-sm text-center">
                                                {t("priority")}
                                            </TableHead>
                                            {activeDepartments.map((dept) => (
                                                <TableHead
                                                    key={dept.key}
                                                    className="text-center font-semibold text-sm min-w-24"
                                                >
                                                    {dept.label}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {tableData.map((row) => (
                                            <TableRow
                                                key={row.priority}
                                                className="hover:bg-muted/30 transition-colors"
                                            >
                                                <TableCell className="py-2">
                                                    <div className="flex justify-center items-center">
                                                        <div className="flex h-16 w-16 items-center justify-center rounded bg-primary/10 text-lg font-bold text-primary">
                                                            {row.priority}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                {activeDepartments.map((dept) => {
                                                    const count = row.counts[dept.key];
                                                    return (
                                                        <TableCell key={dept.key} className="p-1.5">
                                                            <div
                                                                className="flex h-16 w-full cursor-pointer items-center justify-center rounded-md text-lg font-bold text-white shadow-sm transition-all hover:scale-105"
                                                                style={{
                                                                    backgroundColor: getCellColor(row.priority, count),
                                                                }}
                                                                onClick={() => {
                                                                    handleOpenSettingPriorityLeadsPage(
                                                                        dept.key,
                                                                        row.priority
                                                                    );
                                                                }}
                                                            >
                                                                {count}
                                                            </div>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                        {/* Totals row */}
                                        <TableRow className="bg-muted/60 font-semibold hover:bg-muted/60">
                                            <TableCell className="py-2">
                                                <div className="flex justify-center items-center font-semibold">
                                                    {t("total")}
                                                </div>
                                            </TableCell>
                                            {activeDepartments.map((dept) => {
                                                const count = totalsByDepartment[dept.key] ?? 0;
                                                return (
                                                    <TableCell key={dept.key} className="p-1.5">
                                                        <div className="flex h-12 w-full items-center justify-center rounded-md text-lg font-bold text-primary shadow-sm">
                                                            {count}
                                                        </div>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Threshold info per priority */}
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {visiblePriorities.map((prio) => {
                        const { greenMax, yellowMax } = PRIORITY_THRESHOLDS[prio];
                        return (
                            <div key={prio} className="text-xs bg-muted/70 p-2 rounded-md shadow-sm text-center">
                                <span className="font-medium">
                                    {t("priority")} {prio}:
                                </span>{" "}
                                <span style={{ color: REPORT_COLORS.green }}>0–{greenMax}</span>
                                <span className="mx-1">/</span>
                                <span style={{ color: REPORT_COLORS.yellow }}>
                                    {greenMax + 1}–{yellowMax}
                                </span>
                                <span className="mx-1">/</span>
                                <span style={{ color: REPORT_COLORS.red }}>&gt;{yellowMax}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: REPORT_COLORS.green }} />
                        <span>{t("priority_report_green")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: REPORT_COLORS.yellow }} />
                        <span>{t("priority_report_yellow")}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-4 w-4 rounded shrink-0" style={{ backgroundColor: REPORT_COLORS.red }} />
                        <span>{t("priority_report_red")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
